import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function DemandeLogement() {
    const [etudiant, setEtudiant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [typeDemande, setTypeDemande] = useState('postulation');
    const [aChambre, setAChambre] = useState(null);
    const [chambresDisponibles, setChambresDisponibles] = useState([]);
    const [searchChambre, setSearchChambre] = useState('');
    const [selectedChambre, setSelectedChambre] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [quittanceFile, setQuittanceFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [quittancePreview, setQuittancePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [demandeExistante, setDemandeExistante] = useState(null);
    const [tempsRestant, setTempsRestant] = useState(null);
    const [anneeEcoulee, setAnneeEcoulee] = useState(false);
    const [delaiRenouvellement, setDelaiRenouvellement] = useState(null);
    const [renouvellementPossible, setRenouvellementPossible] = useState(false);
    
    const photoInputRef = useRef(null);
    const quittanceInputRef = useRef(null);

    const API_BASE = config.apiBase;

    useEffect(() => {
        fetchEtudiantInfos();
        fetchDemandeExistante();
        fetchChambres();
    }, []);

    // Timer pour le compteur annuel et vérification du délai de renouvellement
    useEffect(() => {
        if (demandeExistante && demandeExistante.statut === 'validee' && demandeExistante.date_fin) {
            const interval = setInterval(() => {
                const now = new Date();
                const fin = new Date(demandeExistante.date_fin);
                const diff = fin - now;
                
                if (diff <= 0) {
                    // L'année est écoulée
                    setTempsRestant({ expired: true });
                    setAnneeEcoulee(true);
                    
                    // Vérifier si on est dans les 10 jours de grâce
                    const finDate = new Date(demandeExistante.date_fin);
                    const diffApresExpiration = now - finDate;
                    const joursApresExpiration = diffApresExpiration / (1000 * 60 * 60 * 24);
                    
                    if (joursApresExpiration <= 10) {
                        // Dans les 10 jours de grâce, renouvellement possible
                        const joursRestants = Math.max(0, 10 - Math.floor(joursApresExpiration));
                        setDelaiRenouvellement(joursRestants);
                        setRenouvellementPossible(true);
                        setTypeDemande('renouvellement');
                    } else {
                        // Plus de 10 jours, plus possible de renouveler
                        setRenouvellementPossible(false);
                        setDelaiRenouvellement(null);
                    }
                    
                    clearInterval(interval);
                } else {
                    const jours = Math.floor(diff / (1000 * 60 * 60 * 24));
                    const heures = Math.floor((diff % (86400000)) / (3600000));
                    setTempsRestant({ jours, heures, expired: false });
                    setAnneeEcoulee(false);
                }
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [demandeExistante]);

    const fetchEtudiantInfos = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/etudiant/get_infos.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setEtudiant(data.etudiant);
                setAChambre(data.a_chambre);
            }
        } catch (error) {
            toast.error('Erreur chargement infos');
        }
    };

    const fetchDemandeExistante = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/etudiant/get_demande_logement.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success && data.demande) {
                setDemandeExistante(data.demande);
                if (data.demande.id_chambre) {
                    setSelectedChambre({ id_chambre: data.demande.id_chambre });
                }
                if (data.demande.statut === 'validee') {
                    setAChambre(true);
                    setTypeDemande('renouvellement');
                }
            }
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchChambres = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/chambres/liste.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setChambresDisponibles(data.chambres);
            }
        } catch (error) {
            toast.error('Erreur chargement chambres');
        }
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleQuittanceChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setQuittanceFile(file);
            setQuittancePreview(URL.createObjectURL(file));
        }
    };

    const uploadFile = async (file, type) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE}/upload_document.php`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
        });
        const data = await response.json();
        return data.url;
    };

    const handleSubmit = async () => {
        if (demandeExistante && demandeExistante.statut === 'validee' && !anneeEcoulee) {
            toast.error('Vous avez déjà une chambre attribuée pour cette année. Renouvelez après expiration.');
            return;
        }
        
        if (!photoFile && !demandeExistante?.photo_url) {
            toast.error("Photo d'identité requise");
            return;
        }
        if (!quittanceFile && !demandeExistante?.quittance_url) {
            toast.error("Quittance d'inscription requise");
            return;
        }

        setSubmitting(true);
        try {
            let photoUrl = demandeExistante?.photo_url;
            let quittanceUrl = demandeExistante?.quittance_url;
            
            if (photoFile) {
                photoUrl = await uploadFile(photoFile, 'carte_identite');
            }
            if (quittanceFile) {
                quittanceUrl = await uploadFile(quittanceFile, 'quittance');
            }
            
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/etudiant/soumettre_demande_logement.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    type_demande: typeDemande,
                    id_chambre: selectedChambre?.id_chambre || null,
                    a_chambre: aChambre,
                    photo_url: photoUrl,
                    quittance_url: quittanceUrl,
                    ancienne_demande_id: demandeExistante?.id_demande || null
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Demande soumise avec succès');
                fetchDemandeExistante();
                setPhotoFile(null);
                setQuittanceFile(null);
                setPhotoPreview(null);
                setQuittancePreview(null);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur lors de la soumission');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredChambres = chambresDisponibles.filter(chambre =>
        `${chambre.bloc} ${chambre.numero}`.toLowerCase().includes(searchChambre.toLowerCase())
    );

    // Interception 1 : Chambre attribuée et valide
    if (demandeExistante && demandeExistante.statut === 'validee' && !anneeEcoulee) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
                <div className="mx-auto max-w-2xl">
                    <div className="rounded-2xl bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white shadow-xl">
                        <h1 className="text-2xl font-bold">🏠 Chambre déjà attribuée</h1>
                        <p className="mt-2 opacity-90">Vous avez déjà une chambre pour l'année académique en cours</p>
                    </div>
                    <div className="mt-6 rounded-2xl bg-white p-6 shadow-xl text-center">
                        <div className="text-6xl mb-4">🏠</div>
                        <p className="text-lg font-semibold text-slate-800">
                            Chambre: {demandeExistante.chambre_nom || `Bloc ${demandeExistante.bloc} - N°${demandeExistante.numero}`}
                        </p>
                        <p className="text-sm text-slate-500 mt-2">
                            Validée jusqu'au {new Date(demandeExistante.date_fin).toLocaleDateString()}
                        </p>
                        {tempsRestant && !tempsRestant.expired && (
                            <div className="mt-4">
                                <div className="mb-2 text-sm text-slate-600">
                                    Temps restant : <strong>{tempsRestant.jours} jours</strong> et <strong>{tempsRestant.heures} heures</strong>
                                </div>
                                <div className="h-2 rounded-full bg-slate-200">
                                    <div className="h-2 rounded-full bg-green-500 transition-all duration-500" style={{ width: `${Math.min(100, (tempsRestant.jours / 365) * 100)}%` }}></div>
                                </div>
                            </div>
                        )}
                        <p className="mt-6 text-amber-600 text-sm bg-amber-50 p-3 rounded-xl border border-amber-200">
                            ⚠️ Vous pourrez renouveler votre demande après expiration de la période de validité.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Interception 2 : Période de grâce pour renouvellement
    if (anneeEcoulee && renouvellementPossible) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
                <div className="mx-auto max-w-4xl">
                    <div className="mb-8 rounded-2xl bg-gradient-to-r from-orange-600 to-red-600 p-6 text-white shadow-xl">
                        <h1 className="text-2xl font-bold">🔄 Renouvellement de chambre</h1>
                        <p className="mt-2 opacity-90">
                            Votre année académique est écoulée. Il vous reste {delaiRenouvellement} jours pour renouveler votre chambre.
                        </p>
                    </div>
                    
                    <div className="rounded-2xl bg-white p-6 shadow-xl mb-6">
                        <h3 className="text-lg font-semibold mb-4">📋 Anciennes informations</h3>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-sm text-slate-500">Ancienne chambre</p>
                                <p className="font-semibold">{demandeExistante?.chambre_nom || `Bloc ${demandeExistante?.bloc} - N°${demandeExistante?.numero}`}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-sm text-slate-500">Ancienne année</p>
                                <p className="font-semibold">{demandeExistante?.annee_academique}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="rounded-2xl bg-white p-6 shadow-xl">
                        <p className="text-center text-slate-500 mb-4">Veuillez mettre à jour vos documents pour le renouvellement</p>
                        
                        <div className="mb-6 grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-slate-700">Nouvelle carte d'identité (si changée)</label>
                                <div onClick={() => photoInputRef.current?.click()} className="cursor-pointer rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 p-4 text-center transition hover:border-blue-500">
                                    <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                                    {photoPreview ? (
                                        <img src={photoPreview} alt="Carte" className="mx-auto max-h-32 rounded" />
                                    ) : demandeExistante?.photo_url ? (
                                        <div>
                                            <div className="text-2xl">✅</div>
                                            <p className="text-sm text-green-600">Garder l'ancien document</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="text-3xl">📄</div>
                                            <p className="text-sm text-slate-500">Cliquez pour modifier</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-semibold text-slate-700">Nouvelle quittance d'inscription</label>
                                <div onClick={() => quittanceInputRef.current?.click()} className="cursor-pointer rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 p-4 text-center transition hover:border-blue-500">
                                    <input ref={quittanceInputRef} type="file" accept="image/*,.pdf" onChange={handleQuittanceChange} className="hidden" />
                                    {quittancePreview ? (
                                        <img src={quittancePreview} alt="Quittance" className="mx-auto max-h-32 rounded" />
                                    ) : demandeExistante?.quittance_url ? (
                                        <div>
                                            <div className="text-2xl">✅</div>
                                            <p className="text-sm text-green-600">Garder l'ancien document</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="text-3xl">📑</div>
                                            <p className="text-sm text-slate-500">Cliquez pour modifier</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <button onClick={handleSubmit} disabled={submitting} className="w-full rounded-xl bg-gradient-to-r from-orange-600 to-red-600 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-50">
                            {submitting ? 'Soumission...' : '🔄 Renouveler ma chambre'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Interception 3 : Période dépassée (perte des droits)
    if (anneeEcoulee && !renouvellementPossible) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
                <div className="mx-auto max-w-2xl">
                    <div className="rounded-2xl bg-gradient-to-r from-red-600 to-pink-600 p-6 text-white shadow-xl">
                        <h1 className="text-2xl font-bold">⏰ Délai expiré</h1>
                        <p className="mt-2 opacity-90">Le délai de renouvellement de 10 jours est dépassé</p>
                    </div>
                    <div className="mt-6 rounded-2xl bg-white p-6 shadow-xl text-center">
                        <p className="text-slate-600 mb-6">
                            Vous n'avez pas renouvelé votre chambre dans les 10 jours suivant son expiration globale. 
                            La chambre a été libérée pour les autres étudiants.
                        </p>
                        <button
                            onClick={() => {
                                setAnneeEcoulee(false);
                                setRenouvellementPossible(false);
                                setAChambre(null);
                                setSelectedChambre(null);
                                setTypeDemande('postulation');
                                fetchDemandeExistante();
                            }}
                            className="rounded-xl bg-blue-600 px-6 py-2.5 font-semibold text-white transition hover:bg-blue-700 shadow-md"
                        >
                            📝 Faire une nouvelle demande
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Gestion du chargement
    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-slate-50">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600"></div>
            </div>
        );
    }

    // Rendu du formulaire normal principal (Postulation initiale ou consultation du statut en attente/rejeté)
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-4xl">
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white shadow-xl">
                    <h1 className="text-2xl font-bold">🏠 Demande de logement</h1>
                    <p className="mt-2 opacity-90">
                        {typeDemande === 'postulation' ? 'Première demande de chambre' : 'Renouvellement de chambre'}
                    </p>
                </div>

                <div className="rounded-2xl bg-white p-6 shadow-xl">
                    {/* Statut de la demande existante non-validée (En attente ou rejetée) */}
                    {demandeExistante && demandeExistante.statut !== 'validee' && (
                        <div className={`mb-6 rounded-xl p-4 border text-sm font-medium ${
                            demandeExistante.statut === 'en_attente' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
                            demandeExistante.statut === 'rejetee' ? 'bg-red-50 border-red-200 text-red-800' :
                            'bg-green-50 border-green-200 text-green-800'
                        }`}>
                            <span>Statut de votre demande active : </span>
                            <span className="capitalize font-bold">
                                {demandeExistante.statut === 'en_attente' ? 'En attente de traitement' : 'Rejetée'}
                            </span>
                            {demandeExistante.commentaire && (
                                <p className="mt-2 text-xs italic opacity-90">Motif : {demandeExistante.commentaire}</p>
                            )}
                        </div>
                    )}

                    {/* Sélection du type de demande */}
                    <div className="mb-6">
                        <label className="mb-2 block text-sm font-semibold text-slate-700">Type de demande</label>
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={() => setTypeDemande('postulation')}
                                disabled={demandeExistante?.statut === 'validee'}
                                className={`flex-1 rounded-xl py-2 font-semibold transition ${
                                    typeDemande === 'postulation' ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                📝 Postulation (Nouvelle chambre)
                            </button>
                            <button
                                type="button"
                                onClick={() => setTypeDemande('renouvellement')}
                                disabled={!demandeExistante || demandeExistante.statut !== 'validee'}
                                className={`flex-1 rounded-xl py-2 font-semibold transition ${
                                    typeDemande === 'renouvellement' ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                🔄 Renouvellement
                            </button>
                        </div>
                    </div>

                    {/* Possession actuelle de chambre */}
                    <div className="mb-6">
                        <label className="mb-2 block text-sm font-semibold text-slate-700">Avez-vous déjà une chambre occupée ?</label>
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={() => setAChambre(true)}
                                disabled={typeDemande === 'renouvellement'}
                                className={`flex-1 rounded-xl py-2 font-semibold transition ${
                                    aChambre === true ? 'bg-green-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                ✅ Oui, j'occupe déjà une chambre
                            </button>
                            <button
                                type="button"
                                onClick={() => setAChambre(false)}
                                disabled={typeDemande === 'renouvellement'}
                                className={`flex-1 rounded-xl py-2 font-semibold transition ${
                                    aChambre === false ? 'bg-orange-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                🔍 Non, je cherche une attribution
                            </button>
                        </div>
                    </div>

                    {/* Moteur de recherche de chambre si l'étudiant déclare en avoir déjà une */}
                    {aChambre === true && (
                        <div className="mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <label className="mb-2 block text-sm font-semibold text-slate-700">Rechercher votre chambre actuelle</label>
                            <input
                                type="text"
                                placeholder="🔍 Taper le nom du bloc ou le numéro..."
                                value={searchChambre}
                                onChange={(e) => setSearchChambre(e.target.value)}
                                className="mb-3 w-full rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-blue-400 bg-white"
                            />
                            <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white">
                                {filteredChambres.map((chambre) => (
                                    <div
                                        key={chambre.id_chambre}
                                        onClick={() => setSelectedChambre(chambre)}
                                        className={`cursor-pointer p-3 border-b border-slate-100 last:border-none transition hover:bg-slate-50 ${
                                            selectedChambre?.id_chambre === chambre.id_chambre ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                        }`}
                                    >
                                        <div className="font-medium text-slate-800">
                                            {chambre.bloc} - Chambre {chambre.numero}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            Occupation : {chambre.occupation} / {chambre.capacite} places
                                        </div>
                                    </div>
                                ))}
                                {filteredChambres.length === 0 && (
                                    <div className="p-4 text-center text-slate-500 text-sm">Aucune chambre trouvée</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Zone de téléversement des justificatifs */}
                    <div className="mb-6 grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-slate-700">Carte d'identité / Attestation</label>
                            <div onClick={() => photoInputRef.current?.click()} className="cursor-pointer rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 p-4 text-center transition hover:border-blue-500 h-40 flex flex-col justify-center items-center">
                                <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Aperçu carte" className="max-h-32 rounded object-contain shadow" />
                                ) : demandeExistante?.photo_url ? (
                                    <div>
                                        <div className="text-2xl">✅</div>
                                        <p className="text-xs text-green-600 font-medium">Fichier déjà sauvegardé</p>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="text-3xl mb-1">📄</div>
                                        <p className="text-xs text-slate-500">Ajouter une pièce d'identité</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-semibold text-slate-700">Quittance de droits d'inscription</label>
                            <div onClick={() => quittanceInputRef.current?.click()} className="cursor-pointer rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 p-4 text-center transition hover:border-blue-500 h-40 flex flex-col justify-center items-center">
                                <input ref={quittanceInputRef} type="file" accept="image/*,.pdf" onChange={handleQuittanceChange} className="hidden" />
                                {quittancePreview ? (
                                    <img src={quittancePreview} alt="Aperçu quittance" className="max-h-32 rounded object-contain shadow" />
                                ) : demandeExistante?.quittance_url ? (
                                    <div>
                                        <div className="text-2xl">✅</div>
                                        <p className="text-xs text-green-600 font-medium">Fichier déjà sauvegardé</p>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="text-3xl mb-1">📑</div>
                                        <p className="text-xs text-slate-500">Parcourir le fichier (PDF/Image)</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Bouton de validation final */}
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 font-semibold text-white transition hover:opacity-95 shadow-md disabled:opacity-50"
                    >
                        {submitting ? 'Traitement et téléversement...' : '🚀 Soumettre ma demande de logement'}
                    </button>
                </div>
            </div>
        </div>
    );
}